import { useState } from 'react';
import { faTable } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { useCurrentEditor } from '@tiptap/react';

import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { IconTooltipButton } from '@/components/ui/icon-tooltip-button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MutedText } from '@/components/ui/typography';

const MAX_ROWS = 6;
const MAX_COLUMNS = 6;

interface TableMenuProps {
  onOpenChange?: (open: boolean) => void;
}

/** Table insertion grid shown in the rich-text formatting toolbar. */
export function TableMenu({ onOpenChange }: TableMenuProps = {}) {
  const { editor } = useCurrentEditor();
  const [open, setOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState(1);
  const [selectedColumns, setSelectedColumns] = useState(1);

  if (!editor) {
    return null;
  }

  const updateOpen = (nextOpen: boolean) => {
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const insertTable = (rows: number, columns: number) => {
    editor.chain().focus().insertTable({ rows, cols: columns, withHeaderRow: false }).run();
    updateOpen(false);
  };

  return (
    <Popover modal open={open} onOpenChange={updateOpen}>
      <PopoverTrigger asChild>
        <IconTooltipButton tooltip="Insert table" size="icon-sm">
          <Icon icon={faTable} />
        </IconTooltipButton>
      </PopoverTrigger>
      <PopoverContent
        data-rich-text-editor-ui="format-toolbar-submenu"
        // A modal popover swallows the click that dismisses it, so the editor would
        // never regain focus and the floating toolbar would unmount along with it.
        onCloseAutoFocus={() => {
          if (!editor.isDestroyed) editor.commands.focus();
        }}
        sideOffset={5}
        align="center"
        className="w-auto p-3"
      >
        <div className="grid gap-2">
          <MutedText className="text-center text-xs font-medium">
            {selectedRows} x {selectedColumns}
          </MutedText>
          <div className="grid grid-cols-6 gap-1">
            {Array.from({ length: MAX_ROWS }).flatMap((_, rowIndex) =>
              Array.from({ length: MAX_COLUMNS }).map((__, columnIndex) => {
                const rows = rowIndex + 1;
                const columns = columnIndex + 1;
                const selected = rows <= selectedRows && columns <= selectedColumns;

                return (
                  <Button
                    key={`${rows}-${columns}`}
                    size="icon-sm"
                    variant={selected ? 'solid' : 'outline'}
                    colorScheme={selected ? 'primary' : 'secondary'}
                    shape="sharp"
                    className="size-5"
                    aria-label={`Insert ${rows} by ${columns} table`}
                    onMouseEnter={() => {
                      setSelectedRows(rows);
                      setSelectedColumns(columns);
                    }}
                    onFocus={() => {
                      setSelectedRows(rows);
                      setSelectedColumns(columns);
                    }}
                    onClick={() => insertTable(rows, columns)}
                  />
                );
              }),
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
